
import React from 'react';
import { motion } from 'framer-motion';
import { CopilotAssistant } from '@/hooks/ai-agents/types';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import CopilotIcon from '@/components/icons/CopilotIcon';
import { cn } from '@/lib/utils';

interface ProfilePictureProps {
  activeAssistant: CopilotAssistant | null;
  isCompact: boolean;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({ 
  activeAssistant,
  isCompact
}) => {
  const hasProfilePicture = activeAssistant && activeAssistant.profile_picture_url;
  
  return (
    <motion.div 
      className={cn(
        "relative", 
        isCompact ? "mr-3" : "mb-5"
      )}
      layoutId="profile-picture"
    >
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-blue-100/40 to-purple-100/40 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full blur-xl"
        layoutId="profile-glow"
        style={{
          scale: isCompact ? 0.75 : 1,
          transformOrigin: "center center"
        }}
      />
      
      {/* Profile picture container */}
      <motion.div 
        className="relative flex items-center justify-center rounded-full bg-gradient-to-br from-white/90 to-white/70 dark:from-slate-800/90 dark:to-slate-900/70 shadow-xl backdrop-blur-sm border border-white/20 dark:border-slate-700/30 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-blue-200/50 dark:group-hover:shadow-blue-900/30"
        layoutId="profile-container"
        style={{
          width: isCompact ? "2.5rem" : "6rem", // 40px in compact, 96px in expanded
          height: isCompact ? "2.5rem" : "6rem",
          margin: isCompact ? "0" : "0 auto"
        }}
      >
        {hasProfilePicture ? (
          <OptimizedImage 
            src={activeAssistant!.profile_picture_url!}
            alt={activeAssistant!.name}
            className="w-full h-full rounded-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder.svg';
            }}
            showSkeleton={true}
          />
        ) : (
          <CopilotIcon 
            size={isCompact ? 16 : 28} 
            strokeWidth={1.5} 
            className="text-blue-500 dark:text-blue-400" 
          />
        )}
      </motion.div>
    </motion.div>
  );
};

export default ProfilePicture;
